import React from 'react'
import {
	Box, Card, CardHeader, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	IconButton, Grid, Chip
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DEMO_SEARCHES = [
	{
		searchDate: '12 Mar 2025',
		pickupOffice: { name: 'Madrid Barajas Airport', country: 'ES', icon: 'plane-departure' },
		dropoffOffice: null,
		sites: ['rentalspot.demo', 'pricefly.demo', 'bookride.demo', 'swiftfleet.demo', 'eurodrive.demo'],
		dor: ['15 Mar 2025', '22 Mar 2025'],
		lor: [3, 5, 7],
	},
	{
		searchDate: '11 Mar 2025',
		pickupOffice: { name: 'Barcelona El Prat Airport', country: 'ES', icon: 'plane-departure' },
		dropoffOffice: { name: 'Madrid Barajas Airport', country: 'ES', icon: 'plane-arrival' },
		sites: ['rentalspot.demo', 'findwheels.demo', 'pricefly.demo', 'autolink.demo'],
		dor: ['20 Mar 2025'],
		lor: [5, 10],
	},
	{
		searchDate: '10 Mar 2025',
		pickupOffice: { name: 'London Heathrow Airport', country: 'GB', icon: 'plane-departure' },
		dropoffOffice: null,
		sites: ['rentalspot.demo', 'pricefly.demo', 'bookride.demo', 'alphacars.demo', 'horizoncars.demo', 'peakrentals.demo'],
		dor: ['01 Apr 2025', '05 Apr 2025', '10 Apr 2025'],
		lor: [7],
	},
	{
		searchDate: '09 Mar 2025',
		pickupOffice: { name: 'Paris Charles de Gaulle', country: 'FR', icon: 'plane-departure' },
		dropoffOffice: null,
		sites: ['rentalspot.demo', 'findwheels.demo', 'swiftfleet.demo'],
		dor: ['25 Mar 2025'],
		lor: [3, 5],
	},
	{
		searchDate: '08 Mar 2025',
		pickupOffice: { name: 'Rome Downtown Office', country: 'IT', icon: 'city' },
		dropoffOffice: { name: 'Milan Central Station', country: 'IT', icon: 'train' },
		sites: ['rentalspot.demo', 'pricefly.demo', 'bookride.demo', 'eurodrive.demo', 'alphacars.demo'],
		dor: ['15 Apr 2025', '20 Apr 2025'],
		lor: [4, 7],
	},
]

const FLAG_EMOJI = { ES: '\ud83c\uddea\ud83c\uddf8', GB: '\ud83c\uddec\ud83c\udde7', FR: '\ud83c\uddeb\ud83c\uddf7', IT: '\ud83c\uddee\ud83c\uddf9' }

function OfficeCell({ pickup, dropoff }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
			<Typography sx={{ fontSize: '0.85rem' }}>{FLAG_EMOJI[pickup.country]}</Typography>
			<IconSeeker icon={pickup.icon} sx={{ fontSize: 13, color: 'primary.main' }} />
			<Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{pickup.name}</Typography>
		</Box>
		{dropoff && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
			<Typography sx={{ fontSize: '0.85rem' }}>{FLAG_EMOJI[dropoff.country]}</Typography>
			<IconSeeker icon={dropoff.icon} sx={{ fontSize: 13, color: 'secondary.main' }} />
			<Typography sx={{ fontSize: '0.72rem', color: 'secondary.light' }}>{dropoff.name}</Typography>
		</Box>}
	</Box>
}

function SitesCell({ sites }) {
	const visible = sites.slice(0, 3)
	const extra = sites.length - 3
	return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
		{visible.map((s, i) => (
			<Chip key={i} label={s} size="small" sx={{ fontSize: '0.68rem', height: 22 }} />
		))}
		{extra > 0 && <Chip label={`+${extra}`} size="small" color="primary" sx={{ fontSize: '0.68rem', height: 22 }} />}
	</Box>
}


export default function HistoryDemoData() {
	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardHeader title={
					<Typography variant="subtitle1" fontWeight={600}>Historical List</Typography>
				} />

				<Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap', textAlign: 'center' } }}>
								<TableCell>Date of Search</TableCell>
								<TableCell>Office</TableCell>
								<TableCell>Sites</TableCell>
								<TableCell>Date of Rental</TableCell>
								<TableCell>Length of Rental</TableCell>
								<TableCell>Real Time Searches</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO_SEARCHES.map((s, idx) => (
								<TableRow key={idx} sx={{
									backgroundColor: idx % 2 === 0 ? '#fafafa' : '#f5f5f5',
									'& td': { py: 1.5, textAlign: 'center', fontSize: '0.75rem' },
								}}>
									<TableCell>
										<Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{s.searchDate}</Typography>
									</TableCell>
									<TableCell>
										<OfficeCell pickup={s.pickupOffice} dropoff={s.dropoffOffice} />
									</TableCell>
									<TableCell>
										<SitesCell sites={s.sites} />
									</TableCell>
									<TableCell>
										<Typography sx={{ fontSize: '0.75rem' }}>{s.dor.join(', ')}</Typography>
									</TableCell>
									<TableCell>
										<Typography sx={{ fontSize: '0.75rem' }}>{s.lor.join(', ')}</Typography>
									</TableCell>
									<TableCell>
										<IconButton size="small" title="View in Real Time Searches" sx={{ color: 'info.main', fontSize: '1.4rem' }}>
											<IconSeeker icon="magnifying-glass-arrow-right" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Card>
		</Grid>
	</Grid>
}
